import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "../pages/CustomButton";
import Loader from "../dist/Loader";
import XLSX from "xlsx-js-style";

/* =========================
   MAIN COMPONENT
========================= */
export default function DealDelightFFCount() {
  const [states, setStates] = useState([]);
  const [substates, setSubstates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [giftName, setGiftName] = useState("");
  const printRef = useRef();

  /* ===== MONTH / YEAR ===== */
  const now = new Date();
  const currentMonth = now.toLocaleString("default", { month: "long" });
  const currentYear = now.getFullYear();

  const months = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December",
  ];
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  /* ===== FILTERS ===== */
  const [filters, setFilters] = useState({
    state_id: "",
    substate_id: "",
    division_id: "",
    employee_id: "",
    month: currentMonth,
    year: currentYear,
    start_pob: 0,
    start_multiply_by: 0,
    slab_pob: 0,
    slab_multiply_by: 0,
  });

  /* =========================
     INITIAL LOAD
     — fetch states, substates, divisions in parallel
     — after divisions resolves, auto-select first and fetch schemes
  ========================= */
  useEffect(() => {
    const init = async () => {
      // Parallel: states + substates + divisions
      const [statesRes, substatesRes, divisionsRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/states`, { headers: getAuthHeaders() }),
        axios.get(`${BASE_URL}/api/sub_states`, { headers: getAuthHeaders() }),
        axios.get(`${BASE_URL}/api/divisions`, { headers: getAuthHeaders() }),
      ]);

      setStates(statesRes.data || []);
      setSubstates(substatesRes.data || []);

      const divList = divisionsRes.data || [];
      setDivisions(divList);

      if (divList.length > 0) {
        const firstDivId = divList[0].id;

        // Set division_id in filters first
        setFilters((prev) => ({ ...prev, division_id: firstDivId }));

        // NOW fetch schemes with the actual division id + current month/year
        await fetchSchemes("", "", firstDivId, currentMonth, currentYear);
      }
    };

    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* =========================
     MASTER APIs
  ========================= */
  const fetchSchemes = async (stateId, substateId, divisionId, month, year) => {
    if (!divisionId || !month || !year) {
      setSchemes([]);
      return;
    }
    try {
      const res = await axios.get(`${BASE_URL}/api/scheme`, {
        headers: getAuthHeaders(),
        params: {
          ...(stateId && { state_id: stateId }),
          ...(substateId && { substate_id: substateId }),
          division_id: divisionId,
          monthYear: `${month} ${year}`,
        },
      });
      setSchemes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Scheme API failed", err);
      setSchemes([]);
    }
  };

  const fetchEmployees = async (stateId, substateId, divisionId) => {
    if (!divisionId) {
      setEmployees([]);
      return;
    }
    const res = await axios.get(`${BASE_URL}/api/employees`, {
      headers: getAuthHeaders(),
      params: {
        ...(stateId && { state_id: stateId }),
        ...(substateId && { substate_id: substateId }),
        division_id: divisionId,
      },
    });
    setEmployees(res.data || []);
  };

  /* =========================
     FILTER CHANGE
  ========================= */
  const handleChange = (key, value) => {
    const updated = { ...filters, [key]: value };

    // Reset substate when state changes
    if (key === "state_id") {
      updated.substate_id = "";
    }

    setFilters(updated);

    if (
      key === "state_id" ||
      key === "substate_id" ||
      key === "division_id" ||
      key === "month" ||
      key === "year"
    ) {
      fetchEmployees(updated.state_id, updated.substate_id, updated.division_id);
      fetchSchemes(
        updated.state_id,
        updated.substate_id,
        updated.division_id,
        updated.month,
        updated.year,
      );
    }
  };

  /* =========================
     AUTO APPLY SCHEME
     — runs whenever schemes list OR division_id changes
  ========================= */
  useEffect(() => {
    if (!filters.division_id || !schemes.length) return;

    const scheme = schemes.find(
      (s) =>
        Number(s.division_id) === Number(filters.division_id) &&
        s.scheme_name === "Deal Delight",
    );

    setFilters((prev) => ({
      ...prev,
      start_pob:        scheme?.pob          ?? 0,
      start_multiply_by: scheme?.gift         ?? 0,
      slab_pob:         scheme?.multiple_pob  ?? 0,
      slab_multiply_by: scheme?.multiple_gift ?? 0,
    }));
  }, [schemes, filters.division_id]); // ✅ re-runs when schemes actually load

  /* =========================
     GO BUTTON
  ========================= */
  const handleGo = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BASE_URL}/api/reports/deal-delight-monthy-ff-count`,
        {
          headers: getAuthHeaders(),
          params: {
            ...(filters.state_id && { state_id: filters.state_id }),
            ...(filters.substate_id && { substate_id: filters.substate_id }),
            division_id: filters.division_id,
            ...(filters.employee_id && { employee_id: filters.employee_id }),
            month_year: `${filters.month} ${filters.year}`,
            is_monthly: true,
          },
        },
      );
      setGiftName(res.data?.gift_name || "Deal Delight");
      setRecords(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error(err);
      setRecords([]);
      alert("Failed to load Deal Delight FF Count");
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     EXPORT EXCEL
  ========================= */
  const handleExportExcel = () => {
    if (!records.length) return;

    const fixValue = (val) => {
      const raw = String(val ?? "").trim();
      return /^\d+$/.test(raw) ? Number(raw) : raw;
    };

    const excelData = records.map((r, i) => ({
      "S.No": i + 1,
      "Emp ID": fixValue(r.employee),
      Employee: r.employee_name,
      Manager: r.manager,
      State: r.state,
      "Sub State": r.substate,
      Designation: r.designation,
      Division: r.division,
      HQ: r.hq,
      DOJ: r.doj,
      "Total POB": Number(r.total_pob || 0),
      "DD Count": Number(r.total_gift || 0),
      "Retailer Count": Number(r.retailer_count || 0),
    }));

    excelData.push({
      "S.No": "", "Emp ID": "", Employee: "", Manager: "", State: "",
      "Sub State": "", Designation: "", Division: "", HQ: "", DOJ: "TOTAL",
      "Total POB": records.reduce((s, r) => s + Number(r.total_pob || 0), 0),
      "DD Count": records.reduce((s, r) => s + Number(r.total_gift || 0), 0),
      "Retailer Count": records.reduce((s, r) => s + Number(r.retailer_count || 0), 0),
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    const empColIndex = Object.keys(excelData[0]).indexOf("Emp ID");

    for (let R = 1; R <= range.e.r; ++R) {
      const cell = worksheet[XLSX.utils.encode_cell({ r: R, c: empColIndex })];
      if (cell) {
        const raw = String(cell.v ?? "").trim();
        cell.t = /^\d+$/.test(raw) ? "n" : "s";
      }
    }

    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
      fill: { fgColor: { rgb: "2563EB" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };
    const rowStyle = {
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };
    const totalRowStyle = {
      font: { bold: true },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };

    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!worksheet[cellAddress]) worksheet[cellAddress] = { t: "z", v: "" };
        if (R === 0) worksheet[cellAddress].s = headerStyle;
        else if (R === range.e.r) worksheet[cellAddress].s = totalRowStyle;
        else worksheet[cellAddress].s = rowStyle;
      }
    }

    worksheet["!cols"] = [
      { wch: 8 }, { wch: 14 }, { wch: 28 }, { wch: 24 }, { wch: 15 },
      { wch: 15 }, { wch: 18 }, { wch: 18 }, { wch: 15 }, { wch: 14 },
      { wch: 14 }, { wch: 12 }, { wch: 16 },
    ];
    worksheet["!freeze"] = { xSplit: 0, ySplit: 1 };
    worksheet["!autofilter"] = { ref: `A1:${XLSX.utils.encode_col(range.e.c)}1` };

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Deal Delight");
    XLSX.writeFile(workbook, `Deal_Delight_${filters.month}_${filters.year}.xlsx`);
  };

  /* =========================
     PRINT
  ========================= */
  const handlePrint = () => {
    if (!printRef.current) return;
    const printWindow = window.open("", "_blank", "width=1200,height=800");
    const doc = printWindow.document;
    doc.open();
    doc.write(`
  <html>
  <head>
    <title>Deal Delight Report</title>
    <style>
      body{font-family:Arial;padding:10px}
      table{width:100%;border-collapse:collapse;font-size:12px}
      th,td{border:1px solid #000;padding:4px}
      th{background:#f3f4f6}
    </style>
  </head>
  <body></body>
  </html>
  `);
    const clone = printRef.current.cloneNode(true);
    doc.body.appendChild(clone);
    doc.close();
    setTimeout(() => printWindow.print(), 300);
  };

  /* =========================
     FILTERED SUBSTATES
  ========================= */
  const filteredSubstates = filters.state_id
    ? substates.filter((ss) => String(ss.state_id) === String(filters.state_id))
    : substates;

  return (
    <div className="p-2">
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/25">
          <Loader onCancel={() => setLoading(false)} />
        </div>
      )}
      <EmployeeFilter
        states={states}
        substates={filteredSubstates}
        divisions={divisions}
        employees={employees}
        filters={filters}
        months={months}
        years={years}
        onChange={handleChange}
        onGo={handleGo}
        loading={loading}
      />

      {records.length > 0 && (
        <div className="mt-4">
          <POBTable
            records={records}
            title={`${giftName} – ${filters.month} ${filters.year}`}
            onPrint={handlePrint}
            onExport={handleExportExcel}
            printRef={printRef}
          />
        </div>
      )}
    </div>
  );
}

/* =========================
   FILTER COMPONENT
========================= */
function EmployeeFilter({
  states,
  substates,
  divisions,
  employees,
  filters,
  months,
  years,
  onChange,
  onGo,
  loading,
}) {
  return (
    <div className="bg-white border-2 border-gray-600">
      <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
        <span className="text-blue-700"> FF Wise </span>
        <span className="text-blue-500"> Deal Delight </span>
      </h1>

      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <Select
          label="State"
          value={filters.state_id}
          onChange={(v) => onChange("state_id", v)}
        >
          <option value="">Select State</option>
          {states.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </Select>

        <Select
          label="Sub state"
          value={filters.substate_id}
          onChange={(v) => onChange("substate_id", v)}
        >
          <option value="">Select Sub state</option>
          {substates.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </Select>

        <Select
          label="Division"
          value={filters.division_id}
          onChange={(v) => onChange("division_id", v)}
        >
          {divisions.map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </Select>

        <Select
          label="Employee"
          value={filters.employee_id}
          onChange={(v) => onChange("employee_id", v)}
        >
          <option value="">All</option>
          {employees.map((e) => (
            <option key={e.id} value={e.id}>
              {e.first_name} {e.last_name} {e.employee} | {e.designation || "-"} |{" "}
              {e.division || "-"} | {e.state_name || "-"}
            </option>
          ))}
        </Select>

        <div className="grid grid-cols-[140px_1fr_1fr] items-center gap-2">
          <label>Month</label>
          <select
            className="border px-2 py-1"
            value={filters.month}
            onChange={(e) => onChange("month", e.target.value)}
          >
            {months.map((m) => (
              <option key={m}>{m}</option>
            ))}
          </select>
          <select
            className="border px-2 py-1"
            value={filters.year}
            onChange={(e) => onChange("year", e.target.value)}
          >
            {years.map((y) => (
              <option key={y}>{y}</option>
            ))}
          </select>
        </div>

        <ReadOnly label="POB" value={filters.start_pob} />
        <ReadOnly label="Start Gift" value={filters.start_multiply_by} />
        <ReadOnly label="Multiple POB" value={filters.slab_pob} />
        <ReadOnly label="Multiply Gift" value={filters.slab_multiply_by} />
      </div>

      <div className="flex justify-center py-4">
        <CustomButton
          onClick={onGo}
          disabled={loading || !filters.state_id}
          variant="go"
        >
          {loading ? "Loading..." : "GO"}
        </CustomButton>
      </div>
    </div>
  );
}

/* =========================
   TABLE
========================= */
function POBTable({ records, title, onPrint, onExport, printRef }) {
  if (!records.length) return null;

  const totalPOB = records.reduce((s, r) => s + Number(r.total_pob || 0), 0);
  const totalGift = records.reduce((s, r) => s + Number(r.total_gift || 0), 0);
  const totalRetailerCount = records.reduce((s, r) => s + Number(r.retailer_count || 0), 0);

  return (
    <div ref={printRef} className="border rounded shadow table-wrapper mt-3">
      {title && (
        <div className="px-3 py-2 border-b bg-white">
          <h2 className="text-center font-semibold text-gray-800">{title}</h2>
        </div>
      )}
      <table className="unlock-dcr-leave-table text-sm">
        <thead className="bg-blue-500 text-white">
          <tr>
            {[
              "S.No","Emp ID","Employee","Manager","State","Sub State",
              "Designation","Division","HQ","DOJ","Total POB","DD Count","Retailer Count",
            ].map((h) => (
              <th key={h} className="border px-2 py-1">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {records.map((r, i) => (
            <tr key={i} className="bg-green-50">
              <td className="border px-2 text-center">{i + 1}</td>
              <td className="border px-2">{r.employee}</td>
              <td className="border px-2">{r.employee_name}</td>
              <td className="border px-2">{r.manager}</td>
              <td className="border px-2">{r.state}</td>
              <td className="border px-2">{r.substate}</td>
              <td className="border px-2">{r.designation}</td>
              <td className="border px-2">{r.division}</td>
              <td className="border px-2">{r.hq}</td>
              <td className="border px-2 text-center">{r.doj}</td>
              <td className="border px-2 text-right">{r.total_pob}</td>
              <td className="border px-2 text-right">{r.total_gift}</td>
              <td className="border px-2 text-right">{r.retailer_count}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-blue-700 text-white font-bold">
            <td colSpan={10} className="border px-2 text-right">TOTAL</td>
            <td className="border px-2 text-right">{totalPOB}</td>
            <td className="border px-2 text-right">{totalGift}</td>
            <td className="border px-2 text-right">{totalRetailerCount}</td>
          </tr>
        </tfoot>
      </table>
      <div className="flex justify-center gap-3 mt-3">
        <CustomButton variant="print" label="Print" onClick={onPrint}>🖨</CustomButton>
        <CustomButton variant="export" label="Export" onClick={onExport}>📥</CustomButton>
      </div>
    </div>
  );
}

/* =========================
   REUSABLE
========================= */
const Select = ({ label, value, onChange, children }) => (
  <div className="grid grid-cols-[140px_1fr] items-center gap-2">
    <label>{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="border px-2 py-1"
    >
      {children}
    </select>
  </div>
);

const ReadOnly = ({ label, value }) => (
  <div className="grid grid-cols-[140px_1fr] items-center gap-2">
    <label>{label}</label>
    <input value={value} disabled className="border px-2 py-1 bg-gray-100" />
  </div>
);